/**
 * Event Fields (Spatial View) — shared constants.
 *
 * Kept in one file because two of them are DISPUTED and will change: putting each
 * behind a named constant means the decision lands in one place rather than being
 * hunted through components.
 */

export const EVENT_CATEGORY = {
  ACTIVITY: 'ACTIVITY',
  TRANSACTION: 'TRANSACTION'
};

/** Story §11, design footer, and the BE's own validation all agree on this one. */
export const MAX_SPATIAL_FIELDS = 3;

/**
 * SETTLED at 15 by the Product Manager, 2026-08-25, and enforced server-side
 * (`PUT .../fields` returns 400 "Maximum 15 fields can be selected for On Click").
 *
 * It had three different answers: the design's footer said "the on-click panel has
 * no limit", the story §12 said 15, and the BE plan rejected the 16th. We follow
 * the 15 so the UI never permits what the server refuses — the worse failure is an
 * admin configuring 16 fields and only finding out on save, after the work.
 *
 * NOTE the design copy still reads "no limit" and needs correcting; the footer text
 * in FieldConfigTable is derived from this constant, so it already says 15.
 *
 * `null` here would mean no limit — the component still supports that if the
 * decision ever moves.
 */
export const MAX_ON_CLICK_FIELDS = 15;

/** Story §13 — derived from the key, never stored. Mirrors the BE service. */
export const IDENTIFIER_KEY = {
  [EVENT_CATEGORY.ACTIVITY]: 'activity_id',
  [EVENT_CATEGORY.TRANSACTION]: 'transaction_id'
};

export const isIdentifier = (fieldKey, category) =>
  Boolean(fieldKey) && fieldKey === IDENTIFIER_KEY[category];

/**
 * The event's own timestamp, per category (FIN-13594).
 *
 * Treated exactly like the identifier: always on, in both columns, and its
 * checkboxes locked. A timestamp is a primary attribute of any event — both
 * fields sit at `sort_order = 1`, straight after the identifier — and an event
 * rendered with no time is of little use on the node or in the detail panel.
 *
 * WHAT THE TICKET FOUND: `txn_date_time` was ticked on the TRANSACTION default
 * while `activity_datetime` was not on the ACTIVITY default, so activity events
 * rendered with no time until someone ticked the boxes for every activity event
 * type — including the `default` type every unknown activity_type falls back to.
 *
 * CONFIG KEYS, not wire names. These are what `fp_event_type_field.field_key`
 * holds and what this screen edits. The timeline sees a different name for the
 * transaction one (`transaction_date`) — see EventTimeline/fields.js.
 */
export const TIMESTAMP_KEY = {
  [EVENT_CATEGORY.ACTIVITY]: 'activity_datetime',
  [EVENT_CATEGORY.TRANSACTION]: 'txn_date_time'
};

/* BOTH CATEGORIES (FIN-13594). The timestamp is pinned on the timeline — the
   node's date line and the panel's Timestamp row carry it for every event — so
   a per-type tick no longer changes what is drawn, and a control that changes
   nothing reads as broken. Locked on, like the identifier; the backend already
   treats both keys as required (`ensureRequiredFields`) and leaves them out of
   its caps. The two keys are distinct, so no category argument is needed. */
export const isTimestamp = (fieldKey) =>
  Boolean(fieldKey) && Object.values(TIMESTAMP_KEY).includes(fieldKey);

/**
 * Fields the BACKEND will not let a type be without (raised in review).
 *
 * `ensureRequiredFields` re-adds a missing identifier OR timestamp on every save
 * — `required = List(idKey, dtKey)` for the category — and the screen reloads
 * afterwards, so a deleted row simply reappears, on/on, at sortOrder 1. It also
 * ties with whatever the admin had at index 1, losing their order.
 *
 * So the delete control is offered for neither, in both categories. Presence
 * (this helper) and visibility (`isTimestamp` / `isIdentifier`, which lock the
 * checkboxes) now agree: both fields are always present and always shown.
 */
export const isRequiredField = (fieldKey, category) =>
  isIdentifier(fieldKey, category) ||
  (Boolean(fieldKey) && fieldKey === TIMESTAMP_KEY[category]);

/**
 * The RESERVED technical value of the two system-default types, seeded by
 * `v6.4.0_custom_field_config_for_events.sql` (Jayaraghavan, 2026-09-09) as
 * "System Default Activity" and "System Default Transaction".
 *
 * PREFER THE BE'S `isSystem` FLAG over this string. `EventFieldConfigDao` derives
 * it as `technical_value == "default"` and sends it on every
 * EventTypeConfigResponse, so the server already answers the question and the two
 * cannot drift. This constant exists only as the fallback for a payload that
 * predates the flag — see `isSystemType` below.
 */
export const SYSTEM_DEFAULT_TECHNICAL_VALUE = 'default';

/**
 * Is this the immutable system-default type?
 *
 * The BE enforces three things on it, and each one is a control this screen must
 * NOT offer — a rejected click is a worse experience than an absent button:
 *   • create — "'default' is reserved for system event types"
 *   • update — "The technical value of a system default event type cannot be changed"
 *   • delete — "System default event types cannot be deleted"
 *   • fields — "field keys are fixed and cannot be added or removed.
 *               Only showSpatial/showOnClick may change"
 *
 * NOT the same question as `isDefault`. `isDefault` is the CLIENT'S chosen
 * fallback and is freely reassignable; `isSystem` is the product's own immutable
 * baseline. A system type ships with `is_default = TRUE`, so the two are true
 * together on a fresh install and it would be easy to conflate them — but the
 * moment a client sets their own default, `isDefault` moves and `isSystem` does
 * not.
 */
export const isSystemType = (type) =>
  Boolean(
    type &&
      (type.isSystem || type.technicalValue === SYSTEM_DEFAULT_TECHNICAL_VALUE)
  );

/**
 * The "New" badge: types created SINCE THIS PAGE WAS OPENED.
 *
 * STILL AN ASSUMPTION — neither the PRD nor the story defines it, and the API
 * exposes only `createdAt` (there is no `isNew` on EventTypeConfigResponse).
 *
 * A 7-DAY WINDOW WAS TRIED FIRST AND WAS WRONG (2026-09-03). Every configured
 * type on a working environment tends to be days old, so the badge appeared on
 * all six at once — and a badge on everything distinguishes nothing. The design
 * shows it on a subset, which a fixed window cannot reproduce.
 *
 * "Since you opened the page" matches the only behaviour actually observed: a
 * type you have just added carries the badge. It also degrades sensibly — an
 * admin arriving at the screen sees no badges, because nothing is new TO THEM,
 * and the one they create appears marked.
 *
 * The trade-off is that the badge clears on reload. If it should instead
 * persist until the type has been opened, that needs per-user state nobody
 * stores today — worth confirming with Yogesh before building it.
 *
 * Captured at module evaluation, i.e. once per page load, so every row is
 * judged against the same instant.
 */
export const PAGE_OPENED_AT = Date.now();

/** True when `createdAt` is later than the moment this page was opened. */
export const isNewType = (createdAt, since = PAGE_OPENED_AT) => {
  if (!createdAt) return false;
  // `Date.parse`, not `new Date(...)`. In production this returns NaN for an
  // unparseable string, as the spec says, and the guard below catches it.
  //
  // CORRECTION (measured 2026-09-04): this comment used to claim `Date.parse`
  // was exempt from the suite's MockDate. It is not — jest-setup.js registers
  // `timezone-mock('UTC')`, and its MockDate throws "Unhandled date format" from
  // `Date.parse` just as it does from the constructor. The guard is therefore
  // unreachable under the default test environment; constants.test.js covers it
  // by unregistering timezone-mock for that one assertion.
  const created = Date.parse(createdAt);
  if (Number.isNaN(created)) return false;
  return created >= since;
};
